import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCheck, faFile} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicButtonComponent,
  BicInputComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {ProjectFileControllerService} from '../../../api/api/projectFileController.service';
import {ProjectFile} from '../../../api/model/projectFile';
import {StateConfigService} from '../../../services/state/state-config.service';

/** A project file plus its resolved folder path, so the picker can show and search the full path. */
export interface PickableFile {
  file: ProjectFile;
  path: string;
}

/**
 * Popup zur Auswahl von Projektdateien als KI-Kontext (Mehrfachauswahl mit Suche über den Pfad).
 *
 * Bewusst ohne eigene Vorstellung davon, was mit den Dateien passiert: der Aufrufer bekommt die
 * gewählten `ProjectFile`s und bildet sie auf sein eigenes Format ab. Nutzer: das Workflow-Widget
 * `sf-file-list`, der ki-editor (Hintergrundwissen / Kontext) und die Kontextauswahl von chatBIC
 * (`message-view`). Der Picker schließt sich nach `selected` nicht selbst; das macht der Aufrufer.
 *
 * Lange Namen bleiben in der Liste gekürzt, der volle Name und Pfad stehen im Hover-Tooltip. Kann
 * die Dateiliste nicht geladen werden, meldet der Picker das per Toast und schließt sich (`closed`).
 */
@Component({
  selector: 'app-context-file-picker',
  standalone: true,
  imports: [FaIconComponent, BicPopupComponent, BicInputComponent, BicButtonComponent, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <bic-popup [customClass]="'w-[700px]'" [title]="title" (close)="closed.emit()">
      <div bicPopupContent class="flex flex-col gap-3">
        <bic-input [(value)]="search" [placeholder]="'Datei suchen...'"></bic-input>

        <div class="text-xs text-gray-500">
          {{ selection.size }} ausgewählt@if (remaining !== null) { · max. {{ remaining }} weitere möglich }
        </div>

        <div class="flex flex-col gap-2 max-h-[350px] overflow-y-auto">
          @if (loading) {
            <div class="flex flex-col items-center justify-center py-8 text-gray-400">
              <fa-icon [icon]="faSpinnerThird" class="text-3xl mb-2 animate-spin"></fa-icon>
              <span class="text-sm">Dateien werden geladen…</span>
            </div>
          } @else if (filteredFiles.length === 0) {
            <div class="flex flex-col items-center justify-center py-8 text-gray-400">
              <fa-icon [icon]="faFile" class="text-3xl mb-2"></fa-icon>
              <span class="text-sm">Keine Dateien gefunden.</span>
            </div>
          } @else {
            @for (entry of filteredFiles; track entry.file.id) {
              <button type="button"
                      (click)="toggle(entry)"
                      [disabled]="isDisabled(entry)"
                      [class.border-green-500]="isSelected(entry)"
                      [class.bg-green-50]="isSelected(entry)"
                      [class.border-gray-200]="!isSelected(entry)"
                      [class.hover:bg-gray-50]="!isSelected(entry)"
                      [class.opacity-50]="isDisabled(entry)"
                      class="flex flex-row items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left">
                <div [class.bg-green-500]="isSelected(entry)"
                     [class.border-green-500]="isSelected(entry)"
                     [class.border-gray-300]="!isSelected(entry)"
                     class="w-5 h-5 rounded border flex items-center justify-center shrink-0">
                  @if (isSelected(entry)) {
                    <fa-icon [icon]="faCheck" class="text-white text-xs"></fa-icon>
                  }
                </div>
                <fa-icon [icon]="faFile" class="text-gray-400 shrink-0"></fa-icon>
                <!-- Voller Name und Pfad per Tooltip: in der Liste werden lange Namen gekürzt. -->
                <div class="flex flex-col flex-1 min-w-0"
                     [tooltip]="entry.file.name ?? ''"
                     [tooltipSubtext]="entry.path !== entry.file.name ? entry.path : ''"
                     tooltipDirection="top"
                     [tooltipWidth]="400">
                  <span class="text-sm font-medium text-gray-900 truncate">{{ entry.file.name }}</span>
                  <!-- Pfad nur, wenn er mehr sagt als der Name (Datei im Wurzelverzeichnis) -->
                  @if (entry.path !== entry.file.name) {
                    <span class="text-xs text-gray-500 truncate">{{ entry.path }}</span>
                  }
                </div>
                @if (isAlreadyAdded(entry)) {
                  <span class="text-xs text-green-600 shrink-0">bereits hinzugefügt</span>
                }
              </button>
            }
          }
        </div>
      </div>
      <div bicPopupButtonGroup class="flex flex-row w-full justify-between items-center gap-4">
        <bic-button buttonType="white" (onClick)="closed.emit()">Abbrechen</bic-button>
        <bic-button buttonType="primary" [disabled]="selection.size === 0" (onClick)="confirm()">
          {{ selection.size }} {{ selection.size === 1 ? 'Datei' : 'Dateien' }} hinzufügen
        </bic-button>
      </div>
    </bic-popup>
  `,
})
export class ContextFilePickerComponent implements OnInit {
  @Input() title = 'Datei als Kontext auswählen';
  /** Ids that are already in use — shown as „bereits hinzugefügt" and not selectable again. */
  @Input() excludeIds: string[] = [];
  /** Upper bound for the total (already added + newly selected); `null` = unbegrenzt. */
  @Input() maxTotal: number | null = null;

  @Output() selected = new EventEmitter<ProjectFile[]>();
  @Output() closed = new EventEmitter<void>();

  search = '';
  loading = true;
  files: PickableFile[] = [];
  selection = new Set<string>();

  protected readonly faFile = faFile;
  protected readonly faCheck = faCheck;
  protected readonly faSpinnerThird = faSpinnerThird;

  private readonly fileController = inject(ProjectFileControllerService);
  private readonly state = inject(StateConfigService);
  private readonly toast = inject(BicToastService);

  get filteredFiles(): PickableFile[] {
    const query = this.search.trim().toLowerCase();
    if (!query) return this.files;
    return this.files.filter(entry => entry.path.toLowerCase().includes(query));
  }

  /** Remaining slots, or `null` when there is no limit. */
  get remaining(): number | null {
    if (this.maxTotal === null) return null;
    return Math.max(0, this.maxTotal - this.excludeIds.length - this.selection.size);
  }

  ngOnInit(): void {
    const projectId = this.state.getProjectId();
    if (!projectId) {
      this.files = [];
      this.loading = false;
      return;
    }
    this.fileController.getFiles(projectId).subscribe({
      next: files => {
        this.files = buildFilesWithPaths(files ?? []);
        this.loading = false;
      },
      error: () => {
        // Nicht still eine leere Liste zeigen: das sähe aus wie ein Projekt ohne Dateien.
        this.files = [];
        this.loading = false;
        this.toast.error('Dateien konnten nicht geladen werden.');
        this.closed.emit();
      },
    });
  }

  isSelected(entry: PickableFile): boolean {
    return this.selection.has(entry.file.id ?? '');
  }

  isAlreadyAdded(entry: PickableFile): boolean {
    return this.excludeIds.includes(entry.file.id ?? '');
  }

  isDisabled(entry: PickableFile): boolean {
    if (this.isAlreadyAdded(entry)) return true;
    return !this.isSelected(entry) && this.remaining === 0;
  }

  toggle(entry: PickableFile): void {
    const id = entry.file.id ?? '';
    if (!id) return;
    if (this.selection.has(id)) {
      this.selection.delete(id);
    } else {
      if (this.remaining === 0) return;
      this.selection.add(id);
    }
    // Neues Set: die Template-Bindings hängen an der Referenz.
    this.selection = new Set(this.selection);
  }

  confirm(): void {
    this.selected.emit(this.files.filter(entry => this.selection.has(entry.file.id ?? '')).map(entry => entry.file));
  }
}

/**
 * Resolves each file's folder path from the flat list (`parentId` chain), so the picker can show
 * and search „Ordner/Unterordner/Datei.pdf" instead of just the file name.
 */
export function buildFilesWithPaths(files: ProjectFile[]): PickableFile[] {
  const byId = new Map<string, ProjectFile>();
  for (const file of files) {
    if (file.id) byId.set(file.id, file);
  }

  const pathOf = (file: ProjectFile): string => {
    const parts: string[] = [];
    let current: ProjectFile | undefined = file;
    // Zyklenschutz: ein defekter parentId-Zeiger darf keine Endlosschleife auslösen.
    const seen = new Set<string>();
    while (current && !seen.has(current.id ?? '')) {
      seen.add(current.id ?? '');
      parts.unshift(current.name ?? '');
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return parts.join('/');
  };

  return files
    .filter(file => !!file.id && !isFolder(file))
    .map(file => ({file, path: pathOf(file)}));
}

/** Ordner sind kein Kontext — sie bleiben nur als Pfadsegment ihrer Kinder sichtbar. */
function isFolder(file: ProjectFile): boolean {
  return file.isFolder === true || file.fileType === 'FOLDER';
}
